import { useCallback, useEffect, useMemo } from 'react';
import { useCadCanvasImageDrag } from './useCadCanvasImageDrag';
import { setCadCanvasImageCoordinates, useCadCanvasImageLayer } from './useCadCanvasImageLayer';
import { useCadImageNaturalSize } from './useCadImageNaturalSize';
import {
  cadCanvasImageMeterCorners,
  clampCadCanvasImageTransform,
  readCadCanvasImage,
  readCadCanvasImageSizeHint,
  shouldPaintCadPlanImage,
  writeCadCanvasImageSizeHint,
} from '../utils/cadCanvasImage';
import { projectCadPlanCorners, readCadPlanBounds, unprojectCadPlanLngLat } from '../utils/cadGeometry';

export { CAD_CANVAS_IMAGE_LAYER_ID, CAD_CANVAS_IMAGE_SOURCE_ID } from './useCadCanvasImageLayer';

/**
 * Raster del render en el plano (metros). Contain en `bounds`; los lotes van
 * encima.
 *
 * @param {{
 *   mapInstanceRef: { current: import('maplibre-gl').Map|null },
 *   enabled?: boolean,
 *   project?: object|null,
 *   featureCollection?: object|null,
 *   transform?: object|null,
 *   paintUrl?: string|null,
 *   onTransformChange?: (next: object) => void,
 *   adjustEnabled?: boolean,
 * }} options
 */
export function useCadPlanCanvasImage({
  mapInstanceRef,
  enabled = false,
  project = null,
  featureCollection = null,
  transform = null,
  paintUrl = null,
  onTransformChange,
  adjustEnabled = false,
}) {
  const wantPaint = adjustEnabled || shouldPaintCadPlanImage(project);
  const remote = wantPaint ? readCadCanvasImage(project) : null;
  const url = wantPaint ? (paintUrl || remote?.url || null) : null;
  const sizeHint = useMemo(() => {
    const src = typeof paintUrl === 'string' ? paintUrl.trim() : '';
    if (src.startsWith('blob:') || src.startsWith('data:')) return null;
    return readCadCanvasImageSizeHint(project);
  }, [paintUrl, project]);
  const imageSize = useCadImageNaturalSize(url, sizeHint);
  const bounds = useMemo(
    () => readCadPlanBounds(project, featureCollection),
    [featureCollection, project],
  );
  const pose = useMemo(
    () => clampCadCanvasImageTransform(transform, bounds, imageSize),
    [bounds, imageSize, transform],
  );
  const coordinates = useMemo(() => {
    if (!url || !bounds) return null;
    const meters = cadCanvasImageMeterCorners(bounds, imageSize, pose);
    return projectCadPlanCorners(meters, bounds);
  }, [bounds, imageSize, pose, url]);

  useEffect(() => {
    if (imageSize) writeCadCanvasImageSizeHint(project, imageSize);
  }, [imageSize, project]);

  useCadCanvasImageLayer({
    mapInstanceRef,
    enabled: enabled && Boolean(url),
    url,
    coordinates,
    guideMode: adjustEnabled,
  });

  const unproject = useCallback(
    (lng, lat) => unprojectCadPlanLngLat(lng, lat, bounds),
    [bounds],
  );

  const applyLivePose = useCallback((nextPose) => {
    if (!bounds) return;
    const meters = cadCanvasImageMeterCorners(bounds, imageSize, nextPose);
    const next = projectCadPlanCorners(meters, bounds);
    if (next) setCadCanvasImageCoordinates(mapInstanceRef.current, next);
  }, [bounds, imageSize, mapInstanceRef]);

  useEffect(() => {
    applyLivePose(pose);
  }, [applyLivePose, pose]);

  const handleTransformChange = useCallback((nextPose, options) => {
    onTransformChange?.(
      clampCadCanvasImageTransform(nextPose, bounds, imageSize),
      options,
    );
  }, [bounds, imageSize, onTransformChange]);

  useCadCanvasImageDrag({
    mapInstanceRef,
    enabled: enabled && adjustEnabled && Boolean(url),
    pose,
    unproject,
    onLiveTransform: applyLivePose,
    onTransformChange: handleTransformChange,
  });
}
